import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, FileText, Loader2, Search, UserRound, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  LONGUEUR_MIN_RECHERCHE,
  useRecherche,
  type ResultatRecherche,
  type RubriqueRecherche,
} from '../../lib/useRecherche';

const RUBRIQUES: Record<RubriqueRecherche, { titre: string; icone: LucideIcon }> = {
  apprenants: { titre: 'Apprenants', icone: UserRound },
  organisations: { titre: 'Entreprises et partenaires', icone: Building2 },
  documents: { titre: 'Documents', icone: FileText },
};

interface Props {
  /** Donne le focus au champ dès l'affichage (ouverture sur mobile). */
  autoFocus?: boolean;
  /** Appelé après navigation vers un résultat ou à la fermeture par Échap. */
  onTerminer?: () => void;
}

/**
 * Recherche globale du header. Les rubriques proposées et les liens viennent
 * de l'API, qui applique la matrice de droits et le périmètre de sites : le
 * front n'en décide rien.
 */
export function BarreRecherche({ autoFocus = false, onTerminer }: Props): JSX.Element {
  const navigate = useNavigate();
  const idListe = useId();
  const conteneur = useRef<HTMLDivElement>(null);
  const champ = useRef<HTMLInputElement>(null);
  const [saisie, setSaisie] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(-1);

  const { data, active, enAttente, isError } = useRecherche(saisie);

  const rubriques = useMemo(() => (active ? (data?.rubriques ?? []) : []), [active, data]);
  const resultats = useMemo(() => rubriques.flatMap((r) => r.resultats), [rubriques]);

  // Une nouvelle liste remet la sélection clavier au départ.
  useEffect(() => setActif(-1), [data]);

  useEffect(() => {
    if (!ouvert) return;
    function clicExterieur(evenement: PointerEvent) {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    }
    document.addEventListener('pointerdown', clicExterieur);
    return () => document.removeEventListener('pointerdown', clicExterieur);
  }, [ouvert]);

  function ouvrir(resultat: ResultatRecherche) {
    setOuvert(false);
    setSaisie('');
    champ.current?.blur();
    navigate(resultat.lien);
    onTerminer?.();
  }

  function clavier(evenement: KeyboardEvent<HTMLInputElement>) {
    if (evenement.key === 'Escape') {
      if (ouvert && saisie) {
        setOuvert(false);
      } else {
        setSaisie('');
        onTerminer?.();
      }
      return;
    }
    if (resultats.length === 0) return;
    if (evenement.key === 'ArrowDown') {
      evenement.preventDefault();
      setOuvert(true);
      setActif((prec) => (prec + 1) % resultats.length);
    } else if (evenement.key === 'ArrowUp') {
      evenement.preventDefault();
      setOuvert(true);
      setActif((prec) => (prec <= 0 ? resultats.length - 1 : prec - 1));
    } else if (evenement.key === 'Enter') {
      const cible = resultats[actif >= 0 ? actif : 0];
      if (cible) {
        evenement.preventDefault();
        ouvrir(cible);
      }
    }
  }

  const listeVisible = ouvert && saisie.trim().length > 0;
  const idOption = (index: number) => `${idListe}-option-${index}`;

  let rang = -1;

  return (
    <div ref={conteneur} className="relative w-full min-w-0">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
      />
      <input
        ref={champ}
        type="search"
        role="combobox"
        aria-label="Rechercher un apprenant, une entreprise, un document"
        aria-expanded={listeVisible}
        aria-controls={idListe}
        aria-autocomplete="list"
        aria-activedescendant={listeVisible && actif >= 0 ? idOption(actif) : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        value={saisie}
        onChange={(evenement) => {
          setSaisie(evenement.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
        onKeyDown={clavier}
        placeholder="Rechercher un apprenant, une entreprise, un document…"
        className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-10 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint [&::-webkit-search-cancel-button]:hidden"
      />
      {enAttente && active ? (
        <Loader2
          aria-hidden="true"
          className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-codialis-gray"
        />
      ) : saisie ? (
        <button
          type="button"
          onClick={() => {
            setSaisie('');
            champ.current?.focus();
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
          aria-label="Effacer la recherche"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {listeVisible && (
        <div
          id={idListe}
          role="listbox"
          aria-label="Résultats de recherche"
          className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white py-2 shadow-lg"
        >
          {!active ? (
            <p className="px-4 py-3 text-sm text-codialis-gray">
              Saisir au moins {LONGUEUR_MIN_RECHERCHE} caractères.
            </p>
          ) : isError ? (
            <p className="px-4 py-3 text-sm text-red-700">La recherche n’a pas abouti. Réessayer.</p>
          ) : !data ? (
            <p className="px-4 py-3 text-sm text-codialis-gray">Recherche…</p>
          ) : rubriques.length === 0 ? (
            <p className="px-4 py-3 text-sm text-codialis-gray">
              Aucune rubrique de recherche n’est ouverte à votre profil.
            </p>
          ) : resultats.length === 0 ? (
            <p className="px-4 py-3 text-sm text-codialis-gray">
              Aucun résultat pour « {data.terme} ».
            </p>
          ) : (
            rubriques
              .filter((r) => r.resultats.length > 0)
              .map(({ rubrique, resultats: lignes }) => {
                const { titre, icone: Icone } = RUBRIQUES[rubrique];
                return (
                  <div key={rubrique} role="group" aria-label={titre} className="py-1">
                    <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-codialis-gray">
                      {titre}
                    </div>
                    {lignes.map((resultat) => {
                      rang += 1;
                      const index = rang;
                      return (
                        <div
                          key={`${rubrique}-${resultat.id}`}
                          id={idOption(index)}
                          role="option"
                          aria-selected={index === actif}
                          // Le champ garde le focus : sans cela, le clic le lui
                          // retire avant que l'option ne reçoive l'évènement.
                          onMouseDown={(evenement) => evenement.preventDefault()}
                          onClick={() => ouvrir(resultat)}
                          onMouseEnter={() => setActif(index)}
                          className={`flex min-h-[44px] cursor-pointer items-center gap-3 px-4 py-2 ${
                            index === actif ? 'bg-codialis-sand' : ''
                          }`}
                        >
                          <Icone className="h-4 w-4 shrink-0 text-codialis-blue" aria-hidden="true" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-codialis-navy">
                              {resultat.libelle}
                            </div>
                            {resultat.detail && (
                              <div className="truncate text-xs text-codialis-gray">{resultat.detail}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
}
